import { useEffect, useId, useRef, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { Btn, Banner } from './ui'

export function SettingsFormModal({
  open,
  title,
  subtitle,
  onClose,
  onSave,
  saveLabel = 'Save',
  cancelLabel = 'Cancel',
  readOnly = false,
  saving = false,
  error,
  children,
}: {
  open: boolean
  title: string
  subtitle?: string
  onClose: () => void
  onSave?: () => void
  saveLabel?: string
  cancelLabel?: string
  readOnly?: boolean
  /** Disables the Save button while a save is in flight -- optional and defaulted to
   *  false so every existing caller keeps its current (unguarded) behavior unless it
   *  opts in. Guards against a double-click firing onSave twice; callers whose onSave
   *  calls a non-idempotent endpoint (account creation, role/access writes) should pass
   *  this. */
  saving?: boolean
  /** A failure from this modal's own onSave -- rendered INSIDE the dialog, not as a
   *  page-level Banner. A caller that keeps setting its page banner state on a failed
   *  save (every existing one does) was showing that error underneath this modal's own
   *  z-50 backdrop -- visible only faintly through the 28%-opacity overlay, confirmed
   *  live as "error prompt hides behind partially". Pass the same error message here
   *  too while the modal that caused it is still open. */
  error?: string | null
  children: ReactNode
}) {
  useEffect(() => {
    if (!open) return
    const esc = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-[rgba(15,23,42,0.28)] p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="flex max-h-[min(85vh,720px)] w-full max-w-[560px] flex-col overflow-hidden
                   animate-slide-up rounded-xl border border-slate-200/80 bg-white shadow-xl ring-1 ring-black/5"
      >
        <header className="flex shrink-0 items-start gap-2.5 border-b border-slate-200/80 px-4 py-3">
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-[15px] font-semibold text-slate-900">{title}</h2>
            {subtitle ? <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X size={15} />
          </button>
        </header>

        {error ? (
          <div className="shrink-0 px-4 pt-3">
            <Banner tone="error">{error}</Banner>
          </div>
        ) : null}

        <div className="min-h-0 flex-1 overflow-auto px-4 py-4">
          {children}
        </div>

        <footer className="flex shrink-0 gap-2 border-t border-slate-200/80 px-4 py-3">
          {!readOnly && onSave ? (
            <Btn onClick={onSave} disabled={saving}>{saving ? 'Saving…' : saveLabel}</Btn>
          ) : null}
          <Btn variant="ghost" onClick={onClose}>{readOnly ? 'Close' : cancelLabel}</Btn>
        </footer>
      </div>
    </div>
  )
}

/** Destructive confirm -- same paper overlay as SettingsFormModal, alertdialog so the
 *  warning is announced. Cancel is focused on open; Escape and the backdrop dismiss. */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Delete',
  onConfirm,
  onClose,
  confirming = false,
}: {
  open: boolean
  title: string
  description: string
  confirmLabel?: string
  onConfirm: () => void
  onClose: () => void
  confirming?: boolean
}) {
  const titleId = useId()
  const descId = useId()
  const cancelRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    const esc = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', esc)
    cancelRef.current?.focus()
    return () => window.removeEventListener('keydown', esc)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-[rgba(15,23,42,0.28)] p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !confirming) onClose() }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="w-full max-w-[440px] animate-slide-up rounded-xl border border-slate-200/80 bg-white shadow-xl ring-1 ring-black/5"
      >
        <div className="px-4 py-4">
          <h2 id={titleId} className="text-[15px] font-semibold text-slate-900">{title}</h2>
          <p id={descId} className="mt-1.5 text-sm text-slate-600">{description}</p>
        </div>
        <footer className="flex justify-end gap-2 border-t border-slate-200/80 px-4 py-3">
          <Btn ref={cancelRef} variant="ghost" onClick={onClose} disabled={confirming}>Cancel</Btn>
          <Btn variant="danger" onClick={onConfirm} disabled={confirming}>
            {confirming ? 'Deleting…' : confirmLabel}
          </Btn>
        </footer>
      </div>
    </div>
  )
}
